import {
	BROWSER_STORAGE_KEYS,
	type BrowserStorageKey,
	type BrowserStorageValue,
	type KeyDeclaration,
} from './keys';
import {
	type BrowserStorage,
	type BrowserStorageDeps,
	type StoragePort,
	type Updater,
} from './types';

const BROWSER_STORAGE_PREFIX = 'px:';

/** Orçamento total para tudo que o módulo guarda, em bytes UTF-16. */
export const BROWSER_STORAGE_BUDGET_BYTES = 512 * 1024;

/** Visão sem genérico de uma declaração; `never` no parâmetro aceita qualquer `Ttl<T>`. */
type AnyDeclaration = Omit<KeyDeclaration<unknown>, 'ttl' | 'maxEntries'> & {
	ttl?: number | ((value: never) => number | Date);
	maxEntries?: number;
};

type ValueEnvelope = { v: number; at: number; value: unknown };
type RecordEnvelope = {
	v: number;
	entries: Record<string, { v: unknown; at: number }>;
};

type Snapshot = { raw: string | null; value: unknown };

const KEY_NAMES = Object.keys(BROWSER_STORAGE_KEYS) as BrowserStorageKey[];

const declarationOf = (key: BrowserStorageKey): AnyDeclaration =>
	BROWSER_STORAGE_KEYS[key];

const isRecordKey = (declaration: AnyDeclaration) =>
	declaration.maxEntries !== undefined;

const parse = (raw: string): unknown => {
	try {
		return JSON.parse(raw);
	} catch {
		return undefined;
	}
};

const isObject = (value: unknown): value is Record<string, unknown> =>
	typeof value === 'object' && value !== null;

const bytesOf = (raw: string) => raw.length * 2;

/** Instante da última gravação de um envelope, de valor ou de record. */
const lastWriteOf = (raw: string | null): number => {
	const envelope = raw === null ? undefined : parse(raw);
	if (!isObject(envelope)) return 0;
	if (typeof envelope.at === 'number') return envelope.at;
	if (!isObject(envelope.entries)) return 0;
	return Math.max(
		0,
		...Object.values(envelope.entries).map((entry) =>
			isObject(entry) && typeof entry.at === 'number' ? entry.at : 0,
		),
	);
};

export const createBrowserStorage = (
	deps: BrowserStorageDeps,
): BrowserStorage => {
	let companyId: number | null = null;
	const listeners = new Map<BrowserStorageKey, Set<() => void>>();
	const snapshots = new Map<string, Snapshot>();
	let detachExternal: (() => void) | null = null;

	const portOf = (declaration: AnyDeclaration): StoragePort =>
		declaration.scope === 'tab' ? deps.session : deps.local;

	/** Nome físico, ou `null` quando a chave não pode ser resolvida (company sem transportadora). */
	const nameOf = (key: BrowserStorageKey): string | null => {
		const { scope, version } = declarationOf(key);
		if (scope === 'company') {
			if (companyId === null) return null;
			return `${BROWSER_STORAGE_PREFIX}${key}:c${companyId}:v${version}`;
		}
		return `${BROWSER_STORAGE_PREFIX}${key}:v${version}`;
	};

	const notify = (key: BrowserStorageKey) => {
		listeners.get(key)?.forEach((listener) => listener());
	};

	const expiresAt = (
		declaration: AnyDeclaration,
		value: unknown,
		at: number,
	): number | null => {
		const { ttl } = declaration;
		if (ttl === undefined) return null;
		if (typeof ttl === 'number') return at + ttl;
		const resolved = ttl(value as never);
		return resolved instanceof Date ? resolved.getTime() : resolved;
	};

	const isExpired = (
		declaration: AnyDeclaration,
		value: unknown,
		at: number,
	) => {
		const limit = expiresAt(declaration, value, at);
		return limit !== null && limit <= deps.now();
	};

	/** Decodifica o envelope; `undefined` significa "use o default". */
	const decode = (
		key: BrowserStorageKey,
		declaration: AnyDeclaration,
		raw: string,
	): { value: unknown; expired: boolean } => {
		const envelope = parse(raw);
		if (!isObject(envelope) || envelope.v !== declaration.version) {
			return { value: undefined, expired: false };
		}

		if (isRecordKey(declaration)) {
			const { entries } = envelope as RecordEnvelope;
			if (!isObject(entries)) return { value: undefined, expired: false };
			const value: Record<string, unknown> = {};
			for (const [id, entry] of Object.entries(entries)) {
				if (isObject(entry)) value[id] = entry.v;
			}
			return { value, expired: false };
		}

		const { value, at } = envelope as ValueEnvelope;
		if (typeof at !== 'number') return { value: undefined, expired: false };
		if (isExpired(declaration, value, at)) {
			deps.log('browser_storage:expired', { key });
			return { value: undefined, expired: true };
		}
		return { value, expired: false };
	};

	const read = (key: BrowserStorageKey): unknown => {
		const declaration = declarationOf(key);
		const name = nameOf(key);
		if (name === null) return declaration.defaultValue;

		const port = portOf(declaration);
		const raw = port.getItem(name);
		const cached = snapshots.get(name);
		if (cached && cached.raw === raw) return cached.value;

		if (raw === null) {
			snapshots.set(name, { raw, value: declaration.defaultValue });
			return declaration.defaultValue;
		}

		const { value, expired } = decode(key, declaration, raw);
		if (expired) port.removeItem(name);
		const resolved = value === undefined ? declaration.defaultValue : value;
		snapshots.set(name, { raw: expired ? null : raw, value: resolved });
		return resolved;
	};

	const encodeRecord = (
		declaration: AnyDeclaration,
		previousRaw: string | null,
		next: Record<string, unknown>,
	): string => {
		const previous = previousRaw ? parse(previousRaw) : undefined;
		const previousEntries =
			isObject(previous) && isObject(previous.entries)
				? (previous.entries as RecordEnvelope['entries'])
				: {};
		const now = deps.now();

		const entries: RecordEnvelope['entries'] = {};
		for (const [id, v] of Object.entries(next)) {
			const before = previousEntries[id];
			const unchanged =
				before !== undefined && JSON.stringify(before.v) === JSON.stringify(v);
			entries[id] = { v, at: unchanged ? before.at : now };
		}

		const overflow =
			Object.keys(entries).length - (declaration.maxEntries ?? 0);
		if (overflow > 0) {
			Object.entries(entries)
				.sort(([, a], [, b]) => a.at - b.at)
				.slice(0, overflow)
				.forEach(([id]) => delete entries[id]);
		}

		const envelope: RecordEnvelope = { v: declaration.version, entries };
		return JSON.stringify(envelope);
	};

	const encodeValue = (declaration: AnyDeclaration, value: unknown): string => {
		const envelope: ValueEnvelope = {
			v: declaration.version,
			at: deps.now(),
			value,
		};
		return JSON.stringify(envelope);
	};

	/** Apaga valores vencidos de toda chave registrada, em qualquer transportadora. */
	const pruneExpired = () => {
		for (const key of KEY_NAMES) {
			const declaration = declarationOf(key);
			if (isRecordKey(declaration)) continue;
			const port = portOf(declaration);
			const pattern = `${BROWSER_STORAGE_PREFIX}${key}:`;
			for (const name of port.names()) {
				if (!name.startsWith(pattern)) continue;
				const raw = port.getItem(name);
				if (raw === null) continue;
				const envelope = parse(raw);
				if (!isObject(envelope) || typeof envelope.at !== 'number') continue;
				if (isExpired(declaration, envelope.value, envelope.at)) {
					port.removeItem(name);
					snapshots.delete(name);
				}
			}
		}
	};

	const sizePerName = () => {
		const sizes: Record<string, number> = {};
		for (const port of [deps.local, deps.session]) {
			for (const name of port.names()) {
				if (!name.startsWith(BROWSER_STORAGE_PREFIX)) continue;
				sizes[name] = bytesOf(port.getItem(name) ?? '');
			}
		}
		return sizes;
	};

	const totalBytes = () =>
		Object.values(sizePerName()).reduce((sum, size) => sum + size, 0);

	/**
	 * Apaga cópias de chaves `company` de outras transportadoras, da gravada há
	 * mais tempo para a mais recente, até caber no orçamento. Records não vencem
	 * por ttl, então sem isto cada transportadora visitada ocupa espaço para sempre.
	 */
	const evictOtherCompanies = () => {
		const current = new Set(KEY_NAMES.map(nameOf));
		const candidates = KEY_NAMES.filter(
			(key) => declarationOf(key).scope === 'company',
		)
			.flatMap((key) => {
				const port = portOf(declarationOf(key));
				const pattern = `${BROWSER_STORAGE_PREFIX}${key}:c`;
				return port
					.names()
					.filter((name) => name.startsWith(pattern) && !current.has(name))
					.map((name) => ({ port, name, raw: port.getItem(name) ?? '' }));
			})
			.sort((a, b) => lastWriteOf(a.raw) - lastWriteOf(b.raw));

		let total = totalBytes();
		for (const { port, name, raw } of candidates) {
			if (total <= BROWSER_STORAGE_BUDGET_BYTES) return;
			port.removeItem(name);
			snapshots.delete(name);
			total -= bytesOf(raw);
		}
	};

	const reclaim = () => {
		pruneExpired();
		evictOtherCompanies();
	};

	const enforceBudget = () => {
		if (totalBytes() <= BROWSER_STORAGE_BUDGET_BYTES) return;
		reclaim();
		const sizes = sizePerName();
		deps.log('browser_storage:budget_exceeded', {
			budget_bytes: BROWSER_STORAGE_BUDGET_BYTES,
			total_bytes: Object.values(sizes).reduce((sum, size) => sum + size, 0),
			sizes,
		});
	};

	const persist = (key: BrowserStorageKey, name: string, raw: string) => {
		const port = portOf(declarationOf(key));
		try {
			port.setItem(name, raw);
		} catch {
			reclaim();
			try {
				port.setItem(name, raw);
			} catch {
				deps.log('browser_storage:write_dropped', {
					key,
					bytes: bytesOf(raw),
					sizes: sizePerName(),
				});
				return;
			}
		}
		enforceBudget();
	};

	const set = <K extends BrowserStorageKey>(
		key: K,
		next: Updater<BrowserStorageValue<K>>,
	) => {
		const declaration = declarationOf(key);
		const name = nameOf(key);
		if (name === null) {
			deps.log('browser_storage:write_without_company', { key });
			return;
		}

		const previous = read(key) as BrowserStorageValue<K>;
		const value =
			typeof next === 'function'
				? (next as (prev: BrowserStorageValue<K>) => BrowserStorageValue<K>)(
						previous,
					)
				: next;

		const port = portOf(declaration);
		const raw = isRecordKey(declaration)
			? encodeRecord(
					declaration,
					port.getItem(name),
					value as Record<string, unknown>,
				)
			: encodeValue(declaration, value);

		persist(key, name, raw);
		snapshots.delete(name);
		notify(key);
	};

	const remove = (key: BrowserStorageKey) => {
		const name = nameOf(key);
		if (name === null) return;
		portOf(declarationOf(key)).removeItem(name);
		snapshots.delete(name);
		notify(key);
	};

	const handleExternalChange = (name: string | null) => {
		if (name !== null && !name.startsWith(BROWSER_STORAGE_PREFIX)) return;
		for (const key of KEY_NAMES) {
			if (name === null || nameOf(key) === name) notify(key);
		}
	};

	const subscribe = (key: BrowserStorageKey, listener: () => void) => {
		if (!listeners.has(key)) listeners.set(key, new Set());
		listeners.get(key)!.add(listener);
		detachExternal ??= deps.onExternalChange(handleExternalChange);

		return () => {
			const set = listeners.get(key);
			set?.delete(listener);
			if (set?.size === 0) listeners.delete(key);
			if (listeners.size === 0 && detachExternal) {
				detachExternal();
				detachExternal = null;
			}
		};
	};

	const setCompany = (next: number | null) => {
		if (companyId === next) return;
		companyId = next;
		for (const key of KEY_NAMES) {
			if (declarationOf(key).scope === 'company') notify(key);
		}
	};

	const clearCredentials = () => {
		for (const key of KEY_NAMES) {
			if (declarationOf(key).credential) remove(key);
		}
	};

	return {
		get: (key) => read(key) as never,
		set,
		remove,
		subscribe,
		setCompany,
		clearCredentials,
	};
};
